import { transparentize } from 'polished'

/**
 * There are multiple placeholder values defined here.
 * We did this to make sure the final DefaultTheme type would contain all the new values we are going to use
 * in the new Prism theme. In practice, these placeholder values won't be used, because new components using for example
 * `theme.colors.background.primary[0]` will always use the new Prism design system.
 *
 * If you need to add new colors to the Prism design system, make sure you add placeholder values here as well so that
 * you can find them in the app's theme.
 */

const ALL_OPACITIES = [4, 10, 30, 35, 50, 70, 85, 90, 97, 100]

export const getTintsWithOpacities = (tint: string) => {
  const tints: { [key: number]: string } = {}

  ALL_OPACITIES.forEach(opacity => {
    tints[opacity] = transparentize(1 - opacity / 100, tint)
  })

  return tints
}

export const palette = {
  abbey: '#575859',
  alto: '#dcdcdc',
  amber: '#fd9800',
  azure: '#0490ff',
  black: '#000000',
  blue: '#0091ff',
  concrete: '#f2f2f2',
  gray: '#828282',
  green: '#6dd400',
  indigo: '#6236ff',
  iron: '#dadcdc',
  orange: '#fa6400',
  purple: '#b620e0',
  red: '#e02020',
  tungsten: '#6d7278',
  white: '#ffffff',
  woodsmoke: '#0d0e0f',
  yellow: '#f7b500',
  royalblue: '#5a3cf3',
  oslogray: '#919393',
  dovegray: '#666666',
  antiquebrass: '#ca9578',
  silverchalice: '#a6a6a6',
  scorpion: '#595959',
  cherokee: '#FBE199',
  nutmeg: '#624800',
  chelseagem: '#946C00',
  greynurse: '#edeeed',
  tawny: '#c85000',
  callout: '#FEF7E5',
  keppel: '#36AC91',
  polar: '#ECFBF7',
  bridesmaid: '#FEEFE5',
  bluechark: '#EFEAFF',
  gallery: '#F0F0F0',
}

const textColors = {
  dark: {
    base: palette.black,
    primary: 'rgba(0, 0, 0, 0.85)',
    secondary: 'rgba(0, 0, 0, 0.65)',
    tertiary: 'rgba(0, 0, 0, 0.4)',
    disabled: 'rgba(0, 0, 0, 0.25)',
  },
  light: {
    base: palette.white,
    primary: 'rgba(255, 255, 255, 0.85)',
    secondary: 'rgba(255, 255, 255, 0.65)',
    tertiary: 'rgba(255, 255, 255, 0.4)',
  },
}

const borderColors = {
  dark: {
    ultraHigh: 'rgba(0, 0, 0, 0.2)',
    extraHigh: 'rgba(0, 0, 0, 0.15)',
    high: 'rgba(0, 0, 0, 0.1)',
    base: 'rgba(0, 0, 0, 0.06)',
    low: 'rgba(0, 0, 0, 0.03)',
    transparent: 'rgba(0, 0, 0, 0)',
  },
  light: {
    ultraHigh: 'rgba(255, 255, 255, 0.3)',
    high: 'rgba(255, 255, 255, 0.2)',
    base: 'rgba(255, 255, 255, 0.1)',
    low: 'rgba(255, 255, 255, 0.06)',
  },
}

const backgroundColors = {
  dark: {
    base: '#212121',
  },
  light: {
    base: palette.white,
    primary: '#fdfdfd',
    secondary: '#f9f9f9',
    disabled: '#f1f1f1',
  },
}

const focusColors = {
  generic: 'hsla(24, 100%, 49%, 0.4)',
}

export const tintColors: Record<string | number, string> = {
  default: 'hsl(19, 89%, 55%)',
  1: 'hsl(334, 100%, 38%)',
  2: 'hsl(28, 100%, 37.5%)',
  3: 'hsl(40, 89%, 44%)',
  4: 'hsl(90, 100%, 26%)',
  5: 'hsl(180, 100%, 25%)',
  6: 'hsl(199, 100%, 35%)',
  7: 'hsl(270, 70%, 50%)',
  8: 'hsl(300, 60%, 39%)',
}

/**
 * Color design tokens.
 *
 * Named colors defined in a plain object.
 *
 * For use with the following CSS properties:
 *
 *     color, background-color, border-color, caret-color, border-top-color,
 *     border-bottom-color, border-left-color, border-right-color,
 *     outline-color, fill, stroke
 *
 * @see https://system-ui.com/theme
 */
export const colors = {
  ...palette,
  primary: palette.orange,
  success: palette.green,
  danger: palette.red,
  positive: palette.green,
  negative: palette.red,
  primaryTint: getTintsWithOpacities(palette.orange),
  text: textColors,
  background: backgroundColors,
  border: borderColors,
  focus: focusColors,
  tint: tintColors,
}
